import { useQuery } from "@apollo/client/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_UI_COLORS, DATA_VIZ_COLORS } from "../../../config/chartColors";
import { SyllableAnalyticsDocument } from "../../../generated/graphql";

interface SyllableAnalyticsProps {
  slideId: string;
  studentId?: string;
}

export function SyllableAnalytics({ slideId, studentId }: SyllableAnalyticsProps) {
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(SyllableAnalyticsDocument, {
    variables: {
      slideId,
      studentId: studentId || null,
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-gray-600">Loading syllable analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-error-500">Error loading data: {error.message}</div>
      </div>
    );
  }

  if (!data?.syllableAnalytics || data.syllableAnalytics.totalAttempts === 0) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-gray-600">No pronunciation attempts yet</div>
      </div>
    );
  }

  const { syllableScores, trends, totalAttempts } = data.syllableAnalytics;

  // Prepare data for bar chart
  const barChartData = syllableScores.map((s) => ({
    syllable: s.syllable,
    score: s.averageScore,
  }));

  // Prepare data for trend chart (group by attempt number)
  const trendData: Record<number, Record<string, number | string>> = {};
  for (const t of trends) {
    const attemptData = trendData[t.attemptNumber] || { attempt: t.attemptNumber };
    attemptData[t.syllable] = t.score;
    trendData[t.attemptNumber] = attemptData;
  }
  const trendChartData = Object.values(trendData);

  // Get unique syllables for trend lines
  const uniqueSyllables = Array.from(new Set(trends.map((t) => t.syllable)));

  return (
    <div className="space-y-8">
      {/* Average Scores Bar Chart */}
      <div>
        <h3 className="h5 mb-4">Average Difficulty by Syllable ({totalAttempts} attempts)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barChartData}>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DATA_VIZ_COLORS.primary} stopOpacity={0.8} />
                <stop offset="100%" stopColor={DATA_VIZ_COLORS.secondary} stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="barGradientHover" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DATA_VIZ_COLORS.primary} stopOpacity={1} />
                <stop offset="100%" stopColor={DATA_VIZ_COLORS.secondary} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_UI_COLORS.grid} />
            <XAxis dataKey="syllable" tick={{ fill: CHART_UI_COLORS.axis }} />
            <YAxis
              domain={[0, 100]}
              label={{
                value: "Score",
                angle: -90,
                position: "insideLeft",
                fill: CHART_UI_COLORS.axis,
              }}
              tick={{ fill: CHART_UI_COLORS.axis }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: CHART_UI_COLORS.tooltip.background,
                border: `1px solid ${CHART_UI_COLORS.tooltip.border}`,
                borderRadius: "0.5rem",
              }}
              labelStyle={{ color: CHART_UI_COLORS.tooltip.label }}
              itemStyle={{ color: CHART_UI_COLORS.tooltip.text }}
              cursor={{ fill: `${DATA_VIZ_COLORS.primary}26` }}
            />
            <Bar
              dataKey="score"
              fill="url(#barGradient)"
              activeBar={{
                fill: "url(#barGradientHover)",
                stroke: DATA_VIZ_COLORS.primary,
                strokeWidth: 2,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Color-coded Syllable List */}
      <div>
        <h3 className="h5 mb-4">Syllable Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {syllableScores.map((s, index) => {
            const accentColors = [
              "border-l-primary-500",
              "border-l-accent-500",
              "border-l-secondary-500",
            ];
            const badgeColors = ["badge-primary", "badge-accent", "badge-secondary"];
            return (
              <div
                key={s.syllable}
                className={`panel p-4 border-l-4 ${accentColors[index % accentColors.length]}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg font-semibold text-teal-400">{s.syllable}</span>
                  <span className={badgeColors[index % badgeColors.length]}>
                    Score: {s.averageScore.toFixed(0)}
                  </span>
                </div>
                <div className="text-2xl font-bold text-secondary-200">
                  {s.averageScore.toFixed(1)}
                </div>
                <div className="caption">{s.attemptCount} attempts</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trend Over Time */}
      {totalAttempts > 1 && (
        <div>
          <h3 className="h5 mb-4">Progress Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendChartData}>
              <defs>
                {uniqueSyllables.map((syllable, index) => (
                  <linearGradient
                    key={`gradient-${syllable}`}
                    id={`lineGradient-${index}`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop offset="0%" stopColor={DATA_VIZ_COLORS.primary} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={DATA_VIZ_COLORS.secondary} stopOpacity={0.8} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_UI_COLORS.grid} />
              <XAxis
                dataKey="attempt"
                label={{
                  value: "Attempt Number",
                  position: "insideBottom",
                  offset: -5,
                  fill: CHART_UI_COLORS.axis,
                }}
                tick={{ fill: CHART_UI_COLORS.axis }}
              />
              <YAxis
                domain={[0, 100]}
                label={{
                  value: "Score",
                  angle: -90,
                  position: "insideLeft",
                  fill: CHART_UI_COLORS.axis,
                }}
                tick={{ fill: CHART_UI_COLORS.axis }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: CHART_UI_COLORS.tooltip.background,
                  border: `1px solid ${CHART_UI_COLORS.tooltip.border}`,
                  borderRadius: "0.5rem",
                }}
                labelStyle={{ color: CHART_UI_COLORS.tooltip.label }}
                itemStyle={{ color: CHART_UI_COLORS.tooltip.text }}
              />
              <Legend wrapperStyle={{ color: CHART_UI_COLORS.legend }} />
              {uniqueSyllables.map((syllable, index) => (
                <Line
                  key={syllable}
                  type="monotone"
                  dataKey={syllable}
                  stroke={`url(#lineGradient-${index})`}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
